import {
  clearActiveAuthSession,
  getActiveToken,
  getActiveUser,
  hasTemporaryAuthSession,
  saveActiveUser,
  savePrimaryAuthSession,
} from '../utils/authSession';
import { ensureMediaToken } from '../utils/mediaToken';
import { API_BASE, platformFetch, streamJsonEvents } from '../platform/http';
import type { ProviderType } from '../constants/providerMetadata';
import type {
  StorageCleanupDeleteResult,
  StorageCleanupScanResult,
  StorageBrowseScope,
  StorageObjectQuery,
  StorageObjectDeleteResult,
  StorageObjectListResult,
  StoragePrefixTreeResult,
} from '../components/admin/types';

export type { ProviderType } from '../constants/providerMetadata';

// API 基础地址统一由 platform/http 维护，此处 re-export 保持下游 import 兼容
export { API_BASE };

function getToken(): string | null {
  return getActiveToken();
}

interface UserInfo {
  id: string;
  email: string;
  phone?: string | null;
  name: string | null;
  credits: number;
  apiKey: string;
  role?: string;
  impersonation?: {
    active: true;
    adminUserId: string;
    adminEmail: string | null;
  };
}

interface AuthResponse {
  message: string;
  user: UserInfo;
  token: string;
}

export type GenerationMode = 'text2img' | 'img2img' | 'inpaint';

interface GenerateRequest {
  model: string;
  prompt: string;
  size?: string;
  aspectRatio?: string;
  quality?: string;
  n?: number;
  // 生成模式
  mode?: GenerationMode;
  // 参考图列表（img2img和inpaint模式使用）
  referenceImages?: string[];
  // mask图base64（inpaint模式使用）
  maskImage?: string;
  // 父图片.com 的 job ID（用于迭代谱系）
  parentJobId?: string;
  // 客户端幂等键：同键重放返回首次创建的任务，防止重复扣积分
  clientRequestId?: string;
  // 审核级别
  moderation?: 'auto' | 'low';
  // 生成背景（transparent = 透明背景，仅支持该能力的模型可选项生效）
  background?: 'transparent';
}

export interface GenerateResponse {
  jobId: string;
  status: string;
  message: string;
}

/** 照片修复预设（服务端下发，不含 prompt） */
export interface JobStatus {
  id: string;
  prompt: string;
  model: string;
  size: string;
  quality: string;
  mode: GenerationMode;
  status: string;
  resultUrl: string | null;
  referenceImages: string[] | null;
  maskImage: string | null;
  creditsUsed: number;
  errorMessage: string | null;
  createdAt: string;
  parentJobId?: string | null;
  generation?: number;
}

export interface CreditTransactionItem {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  jobId?: string | null;
  createdAt: string;
}

export interface CreditStats {
  totalEarned: number;
  totalSpent: number;
  monthSpent: number;
}

export interface CreditInfo {
  credits: number;
  stats?: CreditStats;
  transactions: {
    id: string;
    amount: number;
    type: string;
    description: string | null;
    createdAt: string;
  }[];
}

export interface ModelConfig {
  id: string;
  name: string;
  description?: string;
  /** 模型所属公司（按 modelId 前缀判定，前端按公司分组用；未登记为 null） */
  company?: { id: string; label: string } | null;
  credits: {
    low: number;
    medium: number;
    high: number;
  };
  modeCredits?: {
    text2img: number;
    img2img: number;
    inpaint: number;
  };
  enableText2img: boolean;
  enableImg2img: boolean;
  enableInpaint: boolean;
  modeCapabilities?: {
    text2img: {
      enabled: boolean;
      referenceImageMaxCount: null;
      supportedAspectRatios: string[];
    };
    img2img: {
      enabled: boolean;
      referenceImageMaxCount: number;
      supportedAspectRatios: string[];
    };
    inpaint: {
      enabled: boolean;
      referenceImageMaxCount: number;
      supportedAspectRatios: string[];
    };
  };
  qualityCapabilities?: {
    text2img: {
      low: { enabled: boolean; referenceImageMaxCount: null; supportedAspectRatios: string[] };
      medium: { enabled: boolean; referenceImageMaxCount: null; supportedAspectRatios: string[] };
      high: { enabled: boolean; referenceImageMaxCount: null; supportedAspectRatios: string[] };
    };
    img2img: {
      low: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
      medium: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
      high: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
    };
    inpaint: {
      low: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
      medium: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
      high: { enabled: boolean; referenceImageMaxCount: number; supportedAspectRatios: string[] };
    };
  };
  enableStandardSize?: boolean;
  enableHdSize?: boolean;
  enableUhdSize?: boolean;
  enableTransparentBackground?: boolean;
  /** 近24小时滚动窗口可用率（剔除审核类失败）；total=0 表示窗口内无样本，默认按 100% 展示 */
  availability?: { rate: number; total: number };
}

export interface HeroConfig {
  siteName: string;
  heroTitle: string;
  heroSubtitle: string;
  heroFeature1: string;
  heroFeature2: string;
  heroFeature3: string;
}

export interface PublicFeatureFlags {
  directorEnabled: boolean;
  canvasEnabled: boolean;
  ecommerceReplicateEnabled: boolean;
  ecommerceSuiteEnabled: boolean;
  pptEnabled: boolean;
  videoEnabled: boolean;
  photoRestoreEnabled: boolean;
  isAdmin: boolean;
}

export type ApplicationId = 'director' | 'canvas' | 'ecommerceReplicate' | 'ecommerceSuite' | 'ppt' | 'photoRestore';

export type ApplicationCapability = 'imageGeneration' | 'videoGeneration' | 'llm';

/** 公共目录条目：应用中心渲染来源 */
export interface PublicApplication {
  appId: ApplicationId;
  title: string;
  description: string;
  path: string;
  capabilities: ApplicationCapability[];
  enabled: boolean;
  accessible: boolean;
}

/** 管理目录条目：后台应用管理渲染来源 */
export interface ManagedApplication {
  appId: ApplicationId;
  title: string;
  description: string;
  path: string;
  feature: string;
  adminPath?: string;
  capabilities: ApplicationCapability[];
  enabled: boolean;
}

/** 图片运行时配置（平台层） */
export interface ImageSystemConfig {
  imageTaskTimeoutSeconds: number;
  imageWorkerIntervalMs: number;
  updatedAt: string;
}

/** 画布 AI 配置 */
export interface CanvasSystemConfig {
  isEnabled: boolean;
  model: string;
  temperature: number;
  maxTokens: number;
  updatedAt: string;
}

export type VideoApiDocMode = 'text2video' | 'img2video' | 'first_last_frame';

export type VideoApiDurationSpec =
  | { type: 'enum'; values: number[] }
  | { type: 'range'; min: number; max: number; step: number };

export interface VideoApiCapabilityModeInfo {
  enabled: boolean;
  capabilities: {
    audio?: boolean;
    audioReference?: boolean;
    videoReference?: boolean;
    cameraMovement?: boolean;
    motionIntensity?: boolean;
  };
  supportedDurations: VideoApiDurationSpec;
  supportedResolutions: string[];
  supportedAspectRatios: string[];
  maxReferenceImageCount: number;
  maxAudioReferenceCount: number;
  audioReferenceMinDurationSec?: number;
  audioReferenceMaxDurationSec?: number;
  maxVideoReferenceCount: number;
  videoReferenceMinDurationSec?: number;
  videoReferenceMaxDurationSec?: number;
  videoReferenceTotalMaxDurationSec?: number;
  requiresPublicReferenceUrl: boolean;
  inputPolicy: {
    publicUrlOnly: boolean;
    uploadSupported: boolean;
    base64Supported: boolean;
    storageKeySupported: boolean;
  };
}

export interface VideoApiCapabilitiesResponse {
  auth: {
    type: string;
  };
  inputPolicy: {
    uploadSupported: boolean;
    publicUrlOnly: boolean;
    base64Supported: boolean;
    storageKeySupported: boolean;
  };
  models: Array<{
    modelId: string;
    name: string;
    description: string;
    defaults: {
      duration: number;
      resolution: string;
      aspectRatio: string;
    };
    modes: Partial<Record<VideoApiDocMode, VideoApiCapabilityModeInfo>>;
  }>;
}

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  // 委托平台统一请求层：header 构造、Bearer、401 清会话与广播均由 platform/http 处理
  return platformFetch(path, options);
}

function getBaseUrl(): string {
  return API_BASE;
}

export const api = {
  getBaseUrl,
  async register(email: string, password: string, name?: string, verifyCode?: string, inviteCode?: string, captchaId?: string, captchaText?: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, verifyCode, inviteCode, captchaId, captchaText }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '注册失败');
    }

    const data: AuthResponse = await response.json();
    savePrimaryAuthSession(data.user, data.token);
    void ensureMediaToken();
    return data;
  },

  async registerPhone(phone: string, password: string, name?: string, verifyCode?: string, inviteCode?: string, captchaId?: string, captchaText?: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/auth/register-phone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password, name, verifyCode, inviteCode, captchaId, captchaText }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '注册失败');
    }

    const data: AuthResponse = await response.json();
    savePrimaryAuthSession(data.user, data.token);
    void ensureMediaToken();
    return data;
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '登录失败');
    }

    const data: AuthResponse = await response.json();
    savePrimaryAuthSession(data.user, data.token);
    void ensureMediaToken();
    return data;
  },

  async loginPhone(phone: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE}/auth/login-phone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '登录失败');
    }

    const data: AuthResponse = await response.json();
    savePrimaryAuthSession(data.user, data.token);
    void ensureMediaToken();
    return data;
  },

  async getMe(): Promise<UserInfo> {
    const response = await authFetch('/auth/me');
    if (!response.ok) {
      throw new Error('获取用户信息失败');
    }
    const data = await response.json();
    saveActiveUser(data.user);
    return data.user;
  },

  logout() {
    clearActiveAuthSession();
  },

  getSavedUser(): UserInfo | null {
    return getActiveUser<UserInfo>();
  },

  // 刷新 API Key
  async refreshApiKey(): Promise<{ apiKey: string }> {
    const response = await authFetch('/auth/refresh-api-key', {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '刷新 API Key 失败');
    }
    const data = await response.json();
    
    // 更新本地存储的用户信息
    const savedUser = this.getSavedUser();
    if (savedUser) {
      savedUser.apiKey = data.apiKey;
      saveActiveUser(savedUser);
    }
    
    return data;
  },

  isLoggedIn(): boolean {
    return !!getToken();
  },

  isTemporarySession(): boolean {
    return hasTemporaryAuthSession();
  },

  async createUserImpersonationSession(userId: string): Promise<AuthResponse> {
    const response = await authFetch(`/admin/users/${userId}/impersonation`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建代登录会话失败');
    }
    return response.json();
  },

  async generateImage(request: GenerateRequest): Promise<GenerateResponse> {
    const response = await authFetch('/images/generations', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || '生成失败');
    }

    return response.json();
  },

  /** 画布应用的图片生成入口（source='canvas'，任务记录与流水可区分应用来源） */
  async generateCanvasImage(request: GenerateRequest): Promise<GenerateResponse> {
    const response = await authFetch('/canvas/images/generations', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || '生成失败');
    }

    return response.json();
  },

  async getImageList(): Promise<{ data: JobStatus[] }> {
    const response = await authFetch('/images');
    if (!response.ok) throw new Error('获取图片列表失败');
    return response.json();
  },

  async getImage(id: string): Promise<JobStatus> {
    const response = await authFetch(`/images/${id}`);
    if (!response.ok) throw new Error('获取图片详情失败');
    return response.json();
  },

  async deleteImage(id: string): Promise<void> {
    const response = await authFetch(`/images/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '隐藏图片失败');
    }
  },

  // 管理员：获取图片列表（支持分页和筛选）
  async getAdminImageList(params?: {
    page?: number;
    limit?: number;
    userEmail?: string;
    status?: string;
    model?: string;
    source?: string;
    mode?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<{
    data: Array<JobStatus & { userEmail: string; userName: string | null; isHidden: boolean; channelId: string | null; channelName: string | null; source?: string; userId?: string; size?: string; quality?: string; parentJobId?: string | null; generation?: number; hasReferenceImages?: boolean; hasMaskImage?: boolean; title?: string | null; description?: string | null; outputFormat?: string | null; width?: number | null; height?: number | null; isFavorite?: boolean; isPublic?: boolean; viewCount?: number; likeCount?: number; reportCount?: number; isFeatured?: boolean; updatedAt?: string }>;
    pagination: PaginationInfo;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.userEmail) query.set('userEmail', params.userEmail);
    if (params?.status) query.set('status', params.status);
    if (params?.model) query.set('model', params.model);
    if (params?.source) query.set('source', params.source);
    if (params?.mode) query.set('mode', params.mode);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.search) query.set('search', params.search);
    const response = await authFetch(`/admin/images?${query.toString()}`);
    if (!response.ok) throw new Error('获取图片列表失败');
    return response.json();
  },

  // 管理员：软删除图片（支持批量，仅隐藏记录，文件保留）
  async permanentlyDeleteImages(ids: string[]): Promise<{ message: string; hiddenCount: number }> {
    const response = await authFetch('/admin/images/delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除图片失败');
    }
    return response.json();
  },

  async scanAdminStorageOrphans(): Promise<StorageCleanupScanResult> {
    const response = await authFetch('/admin/storage/orphans/scan');
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '扫描孤立文件失败');
    }
    return response.json();
  },

  async deleteAdminStorageOrphans(filePaths: string[]): Promise<StorageCleanupDeleteResult> {
    const response = await authFetch('/admin/storage/orphans/delete', {
      method: 'POST',
      body: JSON.stringify({ filePaths }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '删除孤立文件失败');
    }
    return response.json();
  },

  async getAdminStorageTree(params?: { scope?: StorageBrowseScope; prefix?: string }): Promise<StoragePrefixTreeResult> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.prefix) query.set('prefix', params.prefix);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await authFetch(`/admin/storage/tree${suffix}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '获取存储目录树失败');
    }
    return response.json();
  },

  async getAdminStorageObjects(params?: StorageObjectQuery): Promise<StorageObjectListResult> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.prefix) query.set('prefix', params.prefix);
    if (params?.search) query.set('search', params.search);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.unreferencedOnly) query.set('unreferencedOnly', '1');
    const response = await authFetch(`/admin/storage/objects?${query.toString()}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '获取存储对象列表失败');
    }
    return response.json();
  },

  async deleteAdminStorageObjects(keys: string[]): Promise<StorageObjectDeleteResult> {
    const response = await authFetch('/admin/storage/objects/delete', {
      method: 'POST',
      body: JSON.stringify({ keys }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '删除存储对象失败');
    }
    const data = await response.json();
    return {
      message: data.message || '删除完成',
      ...data.data,
    };
  },

  // 轮询任务状态直到完成或失败
  async pollJobStatus(
    jobId: string,
    onUpdate: (job: JobStatus) => void,
    interval: number = 2000,
    maxAttempts: number = 300, // 最多轮询10分钟
    signal?: AbortSignal
  ): Promise<JobStatus> {
    // 网络类异常（瞬时 5xx / 断网 / 401 刷新瞬间）连续达到阈值才判死；
    // 单次抖动直接终止会把后端仍在执行、最终会成功的任务误标为失败
    const MAX_CONSECUTIVE_FETCH_ERRORS = 3;
    return new Promise((resolve, reject) => {
      let attempts = 0;
      let consecutiveFetchErrors = 0;
      let timer: ReturnType<typeof setTimeout> | null = null;

      // 外部 abort（重新生成时取消旧轮询）：静默终止，不写任何状态
      const onAbort = () => {
        if (timer) clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal?.aborted) {
        onAbort();
        return;
      }
      signal?.addEventListener('abort', onAbort, { once: true });

      const checkStatus = async () => {
        if (signal?.aborted) return;

        attempts++;

        let job: JobStatus;
        try {
          job = await this.getImage(jobId);
        } catch (error) {
          if (signal?.aborted) return;
          consecutiveFetchErrors++;
          if (consecutiveFetchErrors >= MAX_CONSECUTIVE_FETCH_ERRORS || attempts >= maxAttempts) {
            signal?.removeEventListener('abort', onAbort);
            reject(error);
            return;
          }
          timer = setTimeout(checkStatus, interval);
          return;
        }
        if (signal?.aborted) return;
        consecutiveFetchErrors = 0;

        // onUpdate 的错误（节点删除哨兵等业务性异常）不做容错，立即终止
        onUpdate(job);

        if (job.status === 'completed') {
          signal?.removeEventListener('abort', onAbort);
          resolve(job);
          return;
        }

        if (job.status === 'failed') {
          signal?.removeEventListener('abort', onAbort);
          reject(new Error(job.errorMessage || '图片生成失败，请稍后重试'));
          return;
        }

        if (attempts >= maxAttempts) {
          signal?.removeEventListener('abort', onAbort);
          reject(new Error('等待超时，请稍后刷新页面查看结果'));
          return;
        }

        // 继续轮询
        timer = setTimeout(checkStatus, interval);
      };

      checkStatus();
    });
  },

  async getCredits(): Promise<CreditInfo> {
    const response = await authFetch('/credits');
    if (!response.ok) throw new Error('获取积分信息失败');
    return response.json();
  },

  async getCreditTransactions(params?: { page?: number; limit?: number; category?: '' | 'spend' | 'earn' | 'refund' }): Promise<{
    data: { id: string; amount: number; type: string; description: string | null; jobId: string | null; createdAt: string }[];
    pagination: PaginationInfo;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.category) query.set('category', params.category);
    const response = await authFetch(`/credits/transactions?${query.toString()}`);
    if (!response.ok) throw new Error('获取积分记录失败');
    return response.json();
  },

  async redeemCode(code: string): Promise<{ message: string; credits: number; redeemed: number }> {
    const response = await authFetch('/credits/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '兑换失败');
    }
    return response.json();
  },

  // 获取网站名称（公开接口）
  async getSiteName(): Promise<{ siteName: string }> {
    const response = await fetch(`${API_BASE}/config/site-name`);
    if (!response.ok) throw new Error('获取网站名称失败');
    return response.json();
  },

  // 获取首页配置（公开接口）
  async getHeroConfig(): Promise<HeroConfig> {
    const response = await fetch(`${API_BASE}/config/hero`);
    if (!response.ok) throw new Error('获取首页配置失败');
    return response.json();
  },

  async getFeatureFlags(): Promise<PublicFeatureFlags> {
    const response = await authFetch('/config/features');
    if (!response.ok) throw new Error('获取功能开关失败');
    return response.json();
  },

  async getPublicApplications(): Promise<{ data: PublicApplication[] }> {
    const response = await authFetch('/config/applications/public');
    if (!response.ok) throw new Error('获取应用目录失败');
    return response.json();
  },

  async getAdminApplications(): Promise<{ data: ManagedApplication[] }> {
    const response = await authFetch('/config/applications');
    if (!response.ok) throw new Error('获取应用管理列表失败');
    return response.json();
  },

  async getImageSystemConfig(): Promise<{ data: ImageSystemConfig }> {
    const response = await authFetch('/image-system-config');
    if (!response.ok) throw new Error('获取图片配置失败');
    return response.json();
  },

  async updateImageSystemConfig(patch: Partial<ImageSystemConfig>): Promise<{ data: ImageSystemConfig }> {
    const response = await authFetch('/image-system-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || '保存图片配置失败');
    return data;
  },

  async getCanvasSystemConfig(): Promise<{ data: CanvasSystemConfig }> {
    const response = await authFetch('/canvas-system-config');
    if (!response.ok) throw new Error('获取画布 AI 配置失败');
    return response.json();
  },

  async updateCanvasSystemConfig(patch: Partial<CanvasSystemConfig>): Promise<{ data: CanvasSystemConfig }> {
    const response = await authFetch('/canvas-system-config', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || '保存画布 AI 配置失败');
    return data;
  },

  async updateApplicationEnabled(appId: string, enabled: boolean): Promise<{ data: ManagedApplication }> {
    const response = await authFetch(`/config/applications/${appId}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || '更新应用状态失败');
    return data;
  },

  // 获取网站 URL（公开接口）
  async getSiteUrl(): Promise<{ siteUrl: string }> {
    const response = await fetch(`${API_BASE}/config/site-url`);
    if (!response.ok) throw new Error('获取网站 URL 失败');
    return response.json();
  },

  // 获取参考图最大数量配置（公开接口）
  async getReferenceImageMaxCount(): Promise<{ referenceImageMaxCount: number }> {
    const response = await fetch(`${API_BASE}/config/reference-image-max-count`);
    if (!response.ok) return { referenceImageMaxCount: 16 };
    return response.json();
  },

  // 获取默认模型配置（公开接口）
  async getDefaultModel(): Promise<{ defaultModelId: string | null }> {
    const response = await fetch(`${API_BASE}/config/default-model`);
    if (!response.ok) return { defaultModelId: null };
    return response.json();
  },

  // 获取公告自动折叠延时（公开接口，毫秒，0=不折叠）
  async getAnnouncementAutoCollapseDelay(): Promise<{ announcementAutoCollapseDelayMs: number }> {
    const response = await fetch(`${API_BASE}/config/announcement-autocollapse-delay`);
    if (!response.ok) return { announcementAutoCollapseDelayMs: 5000 };
    return response.json();
  },

  // 获取统计代码（公开接口，返回原始 HTML 脚本字符串）
  async getAnalyticsTrackingCode(): Promise<{ analyticsTrackingCode: string }> {
    const response = await fetch(`${API_BASE}/config/analytics-tracking-code`);
    if (!response.ok) return { analyticsTrackingCode: '' };
    return response.json();
  },

  // 获取模型配置列表（公开接口，无需登录）
  async getModels(): Promise<{ data: ModelConfig[] }> {
    const response = await fetch(`${API_BASE}/models/public`);
    if (!response.ok) throw new Error('获取模型列表失败');
    return response.json();
  },

  async getVideoApiCapabilities(apiKey: string): Promise<VideoApiCapabilitiesResponse> {
    const response = await fetch(`${API_BASE}/v1/videos/capabilities`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error?.message || '获取视频能力失败');
    }
    return data?.data as VideoApiCapabilitiesResponse;
  },

  // 管理员：获取所有模型配置
  async getAdminModels(): Promise<{ data: ModelConfigData[] }> {
    const response = await authFetch('/models');
    if (!response.ok) throw new Error('获取模型列表失败');
    return response.json();
  },

  // 管理员：创建模型配置
  async createModel(data: Partial<ModelConfigData>): Promise<{ message: string; data: ModelConfigData }> {
    const response = await authFetch('/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 管理员：更新模型配置
  async updateModel(id: string, data: Partial<ModelConfigData>): Promise<{ message: string; data: ModelConfigData }> {
    const response = await authFetch(`/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 管理员：批量更新模型排序
  async reorderModels(modelIds: string[]): Promise<{ message: string; data: ModelConfigData[] }> {
    const response = await authFetch('/models/reorder', {
      method: 'POST',
      body: JSON.stringify({ modelIds }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '排序更新失败');
    }
    return response.json();
  },

  async getModelRouting(id: string): Promise<{ data: ModelRoutingConfigData }> {
    const response = await authFetch(`/models/${id}/routing`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '获取路由配置失败');
    }
    return response.json();
  },

  async updateModelRouting(id: string, data: Partial<ModelRoutingConfigData>): Promise<{ message: string; data: ModelConfigData }> {
    const response = await authFetch(`/models/${id}/routing`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新路由配置失败');
    }
    return response.json();
  },

  // 管理员：删除模型配置
  async deleteModel(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/models/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },

  getOAuthStatus(): Promise<{ linuxdoEnabled: boolean }> {
    return fetch(`${API_BASE}/oauth-config/status`).then(res => {
      if (!res.ok) throw new Error('获取 OAuth 配置失败');
      return res.json();
    });
  },

  async getOAuthAuthorizeUrl(redirectUrl?: string): Promise<{ authUrl: string; state: string }> {
    const params = new URLSearchParams();
    if (redirectUrl) params.set('redirectUrl', redirectUrl);
    const response = await fetch(`${API_BASE}/oauth/linuxdo/authorize?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '获取授权 URL 失败');
    }
    return response.json();
  },

  // 图形验证码
  async getCaptcha(): Promise<{ captchaId: string; svg: string }> {
    const response = await fetch(`${API_BASE}/auth/captcha`);
    if (!response.ok) throw new Error('获取验证码失败');
    return response.json();
  },

  async getCaptchaEnabled(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/config/captcha-enabled`);
      if (!response.ok) return true;
      const data = await response.json();
      return data.captchaEnabled ?? true;
    } catch {
      return true;
    }
  },
  // ---------------------------------------------------------------- App 发布

  async getAppRelease(): Promise<{
    versionCode: number | null;
    versionName: string;
    notes: string;
    force: boolean;
    apk: { exists: boolean; size: number; updatedAt: string | null };
    apkVersion: { versionCode: number; versionName: string } | null;
  }> {
    const response = await authFetch('/admin/app-release');
    if (!response.ok) throw new Error('获取发布信息失败');
    return response.json();
  },

  async saveAppRelease(payload: {
    versionCode: number;
    versionName: string;
    notes: string;
    force: boolean;
  }): Promise<{ message: string }> {
    const response = await authFetch('/admin/app-release', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || '保存失败');
    }
    return response.json();
  },

  async uploadAppApk(file: File): Promise<{
    message: string;
    apk: { exists: boolean; size: number; updatedAt: string | null };
    apkVersion: { versionCode: number; versionName: string } | null;
  }> {
    const form = new FormData();
    form.append('file', file);

    // 不要手动设 Content-Type，让浏览器带上 multipart 边界
    const response = await authFetch('/admin/app-release/upload', {
      method: 'POST',
      body: form,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || '上传失败');
    }
    return response.json();
  },
};

export interface ModelConfigData {
  id: string;
  modelId: string;
  name: string;
  description: string | null;
  standardCredits: number;
  hdCredits: number;
  uhdCredits: number;
  text2imgCredits: number;
  img2imgCredits: number;
  inpaintCredits: number;
  isActive: boolean;
  sortOrder: number;
  fallbackChannelIds: string[];
  text2imgChannelId: string | null;
  text2imgChannel?: {
    id: string;
    name: string;
  } | null;
  img2imgChannelId: string | null;
  img2imgChannel?: {
    id: string;
    name: string;
  } | null;
  inpaintChannelId: string | null;
  inpaintChannel?: {
    id: string;
    name: string;
  } | null;
  enableText2img: boolean;
  enableImg2img: boolean;
  enableInpaint: boolean;
  enableFallbackChain: boolean;
  appendPrompt: string | null;
  enableStandardSize: boolean;
  enableHdSize: boolean;
  enableUhdSize: boolean;
  enableTransparentBackground: boolean;
  routingOverrides?: {
    text2img: { low: string | null; medium: string | null; high: string | null };
    img2img: { low: string | null; medium: string | null; high: string | null };
    inpaint: { low: string | null; medium: string | null; high: string | null };
  };
  routingSummary?: {
    defaultRuleCount: number;
    overrideCount: number;
  };
}

export interface ModelRoutingConfigData {
  id: string;
  modelId: string;
  name: string;
  fallbackChannelIds: string[];
  text2imgChannelId: string | null;
  img2imgChannelId: string | null;
  inpaintChannelId: string | null;
  enableFallbackChain: boolean;
  routingOverrides: NonNullable<ModelConfigData['routingOverrides']>;
  routingSummary: {
    defaultRuleCount: number;
    overrideCount: number;
  };
}

export interface ApiChannel {
  id: string;
  name: string;
  apiKey: string;
  apiUrl: string;
  apiModelId?: string | null;
  provider: ProviderType;
  isActive: boolean;
  sortOrder: number;
  costPerCallLow: number;
  costPerCallMedium: number;
  costPerCallHigh: number;
  referenceImageMaxCount?: number | null;
  forceQualityMapping?: boolean;
  forcedQuality?: string | null;
  remark?: string | null;
  models?: {
    id: string;
    modelId: string;
    name: string;
  }[];
}

// 渠道健康度窗口统计
export interface ChannelHealthWindow {
  /** 成功率百分比 0-100；样本不足时为 null */
  successRate: number | null;
  /** 参与统计的样本数（completed + 计入的 failed） */
  total: number;
  /** 计入的失败数（已剔除 moderation/param） */
  failed: number;
}

// 渠道健康度（近1h + 近24h 双窗口）
export interface ChannelHealth {
  window1h: ChannelHealthWindow;
  window24h: ChannelHealthWindow;
}

// API 渠道管理 API
export const apiChannelAPI = {
  // 获取所有 API 渠道
  async getChannels(): Promise<{ data: ApiChannel[] }> {
    const response = await authFetch('/channels');
    if (!response.ok) throw new Error('获取 API 渠道列表失败');
    return response.json();
  },

  // 获取渠道健康度（近1h + 近24h 双窗口成功率）
  async getChannelHealth(): Promise<{ data: Record<string, ChannelHealth> }> {
    const response = await authFetch('/channels/health');
    if (!response.ok) throw new Error('获取渠道健康度失败');
    return response.json();
  },

  // 获取单个渠道的完整 API Key（用于复制）
  async getChannelKey(id: string): Promise<{ apiKey: string }> {
    const response = await authFetch(`/channels/${id}/key`);
    if (!response.ok) throw new Error('获取 API Key 失败');
    return response.json();
  },

  // 创建 API 渠道
  async createChannel(data: Partial<ApiChannel>): Promise<{ message: string; data: ApiChannel }> {
    const response = await authFetch('/channels', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 更新 API 渠道
  async updateChannel(id: string, data: Partial<ApiChannel>): Promise<{ message: string; data: ApiChannel }> {
    const response = await authFetch(`/channels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 删除 API 渠道
  async deleteChannel(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/channels/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },

  // 测试 API 渠道
  async testChannel(data: { apiUrl: string; apiKey: string; model?: string; apiModelId?: string }): Promise<{ message: string }> {
    const response = await authFetch('/channels/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '测试失败');
    }
    return response.json();
  },

  // 拉取渠道可用模型列表（apiKey 留空时后端使用 channelId 对应已保存的 Key）
  async fetchModels(params: { apiUrl: string; apiKey?: string; channelId?: string }): Promise<{ data: string[] }> {
    const response = await authFetch('/channels/fetch-models', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '拉取模型列表失败');
    }
    return response.json();
  },

  // ---------------- 图片 Provider / Adapter 管理 ----------------

  /**
   * 获取所有图片 provider 元数据 + isHidden 标记
   */
  async getProviderMeta(): Promise<{ data: ImageProviderMetaInfo[] }> {
    const response = await authFetch('/channels/providers/meta');
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '获取 Adapter 列表失败');
    }
    return response.json();
  },

  /**
   * 切换 provider 是否被隐藏
   *
   * 隐藏只影响管理后台新建/编辑渠道时下拉过滤、Adapter 管理页徽章；
   * 不影响已使用该 provider 的渠道继续生效。
   */
  async setProviderHidden(providerId: string, isHidden: boolean): Promise<void> {
    const response = await authFetch(
      `/channels/providers/${encodeURIComponent(providerId)}/state`,
      {
        method: 'PATCH',
        body: JSON.stringify({ isHidden }),
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '更新 Adapter 状态失败');
    }
  },
};

/**
 * 图片 provider 元数据（与后端 PROVIDER_METADATA 字段对齐 + isHidden）
 */
export interface ImageProviderMetaInfo {
  id: ProviderType;
  label: string;
  badgeLabel: string;
  badgeClassName: string;
  modeSupport: { text2img: boolean; img2img: boolean; inpaint: boolean };
  defaultReferenceImageMaxCount?: number;
  maxReferenceImageMaxCount?: number;
  maxOutputImageCount?: number;
  adminCapability: string;
  adminDetail: string;
  isHidden: boolean;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
  isActive: boolean;
}

// FAQ API
export const faqAPI = {
  // 获取 FAQ 列表（公开）
  async getFAQs(): Promise<{ data: FAQ[] }> {
    const response = await fetch(`${API_BASE}/faq/public`);
    if (!response.ok) throw new Error('获取 FAQ 失败');
    return response.json();
  },

  // 管理员：获取所有 FAQ
  async getAdminFAQs(): Promise<{ data: FAQ[] }> {
    const response = await authFetch('/faq');
    if (!response.ok) throw new Error('获取 FAQ 失败');
    return response.json();
  },

  // 管理员：创建 FAQ
  async createFAQ(data: Partial<FAQ>): Promise<{ message: string; data: FAQ }> {
    const response = await authFetch('/faq', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 管理员：更新 FAQ
  async updateFAQ(id: string, data: Partial<FAQ>): Promise<{ message: string; data: FAQ }> {
    const response = await authFetch(`/faq/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 管理员：删除 FAQ
  async deleteFAQ(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/faq/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },
};

export interface Announcement {
  id: string;
  type: 'warning' | 'info';
  label: string;
  title: string;
  content: string;
  linkText?: string;
  linkUrl?: string;
  bannerImageUrl?: string;
  isVisible: boolean;
  isLoginRequired: boolean;
  sortOrder: number;
}

// 公告 API
export const announcementAPI = {
  // 获取公告列表（公开）
  async getAnnouncements(): Promise<{ data: Announcement[] }> {
    const response = await authFetch('/announcements/public');
    if (!response.ok) throw new Error('获取公告失败');
    return response.json();
  },

  // 管理员：获取所有公告
  async getAdminAnnouncements(): Promise<{ data: Announcement[] }> {
    const response = await authFetch('/announcements');
    if (!response.ok) throw new Error('获取公告失败');
    return response.json();
  },

  // 管理员：创建公告
  async createAnnouncement(data: Partial<Announcement>): Promise<{ message: string; data: Announcement }> {
    const response = await authFetch('/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 管理员：更新公告
  async updateAnnouncement(id: string, data: Partial<Announcement>): Promise<{ message: string; data: Announcement }> {
    const response = await authFetch(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 管理员：删除公告
  async deleteAnnouncement(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/announcements/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },
};

// 系统图片上传（统一后台管理接口）
export const systemMediaAPI = {
  async upload(file: File, scope: 'announcement_banner' | 'director_watermark' | 'template_cover'): Promise<{ storageKey: string; size: number }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('scope', scope);
    const response = await platformFetch('/admin/system-media/upload', {
      method: 'POST',
      body: fd,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || '上传失败');
    }
    return response.json();
  },
};

// AI 提示词优化配置
export interface PromptOptimizeConfig {
  isEnabled: boolean;
  // model 现在存 LlmModel.id（uuid）；apiUrl / apiKey 已迁到 LlmChannel
  model: string;
  systemPromptText2img: string;
  systemPromptImg2img: string;
  systemPromptInpaint: string;
  temperature: number;
  maxTokens: number;
  updatedAt?: string;
}

// AI提示词优化 API
export const promptOptimizeAPI = {
  // 获取AI优化配置状态（公开）
  async getStatus(): Promise<{ isEnabled: boolean }> {
    const response = await fetch(`${API_BASE}/prompt-optimize/status`);
    if (!response.ok) throw new Error('获取状态失败');
    return response.json();
  },

  // 管理员：获取AI优化配置
  async getConfig(): Promise<PromptOptimizeConfig> {
    const response = await authFetch('/prompt-optimize/config');
    if (!response.ok) throw new Error('获取配置失败');
    return response.json();
  },

  // 管理员：更新AI优化配置
  async updateConfig(data: Partial<PromptOptimizeConfig>): Promise<{ message: string } & PromptOptimizeConfig> {
    const response = await authFetch('/prompt-optimize/config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 流式优化提示词（mode 兼容图片三种 + 视频三种）
  async streamOptimize(
    prompt: string,
    mode: GenerationMode | 'text2video' | 'img2video' | 'first_last_frame' = 'text2img',
    onChunk: (chunk: string) => void,
    onError?: (error: string) => void,
    onComplete?: () => void,
    systemPrompt?: string,
    mergePrompt?: string,
    templateText?: string
  ): Promise<void> {
    const response = await platformFetch('/prompt-optimize/stream', {
      method: 'POST',
      body: JSON.stringify({ prompt, mode, systemPrompt, mergePrompt, templateText }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(error.error || '优化失败');
    }

    if (!response.body) {
      throw new Error('无法读取响应');
    }

    await streamJsonEvents(response, {
      onEvent: (parsed) => {
        if (parsed.error) {
          onError?.(parsed.error as string);
        } else if (parsed.content) {
          onChunk(parsed.content as string);
        }
      },
      onDone: () => onComplete?.(),
    });
  },
};

// ==================== 提示词模板广场 API ====================

export interface PromptTemplate {
  id: string;
  title: string;
  description: string | null;
  category: string;
  coverImage: string | null;
  exampleImageUrl: string | null;
  tags: string | null;
  usageCount: number;
  isFeatured: boolean;
  isMemberOnly: boolean;
  defaultModel: string | null;
  defaultSize: string | null;
  defaultQuality: string | null;
  createdAt: string;
}

export interface PromptTemplateDetail extends PromptTemplate {
  templateText: string;
  variables: string | null;
  examplePrompt: string | null;
}

export interface TemplateVariable {
  name: string;
  label: string;
  type: 'text' | 'select' | 'textarea';
  options?: string[];
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
}

export const templateAPI = {
  // 获取模板列表（公开）
  async getTemplates(params?: { category?: string; search?: string; featured?: boolean }): Promise<{ data: PromptTemplate[] }> {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    if (params?.featured) query.set('featured', 'true');
    const response = await fetch(`${API_BASE}/templates/public?${query.toString()}`);
    if (!response.ok) throw new Error('获取模板列表失败');
    return response.json();
  },

  // 获取模板详情（公开）
  async getTemplate(id: string): Promise<{ data: PromptTemplateDetail }> {
    const response = await fetch(`${API_BASE}/templates/public/${id}`);
    if (!response.ok) throw new Error('获取模板详情失败');
    return response.json();
  },

  // 收藏模板
  async favoriteTemplate(id: string): Promise<{ message: string; isFavorite: boolean }> {
    const response = await authFetch(`/templates/${id}/favorite`, { method: 'POST' });
    if (!response.ok) throw new Error('收藏失败');
    return response.json();
  },

  // 取消收藏
  async unfavoriteTemplate(id: string): Promise<{ message: string; isFavorite: boolean }> {
    const response = await authFetch(`/templates/${id}/favorite`, { method: 'DELETE' });
    if (!response.ok) throw new Error('取消收藏失败');
    return response.json();
  },

  // 检查是否已收藏
  async checkFavorite(id: string): Promise<{ isFavorite: boolean }> {
    const response = await authFetch(`/templates/${id}/favorite`);
    if (!response.ok) throw new Error('检查收藏状态失败');
    return response.json();
  },

  // 记录模板使用
  async useTemplate(id: string, finalPrompt: string, imageJobId?: string): Promise<{ message: string }> {
    const response = await authFetch(`/templates/${id}/use`, {
      method: 'POST',
      body: JSON.stringify({ finalPrompt, imageJobId }),
    });
    if (!response.ok) throw new Error('记录使用失败');
    return response.json();
  },

  // 管理员：获取所有模板
  async getAdminTemplates(): Promise<{ data: (PromptTemplateDetail & { _count: { favorites: number; usages: number } })[] }> {
    const response = await authFetch('/templates');
    if (!response.ok) throw new Error('获取模板列表失败');
    return response.json();
  },

  // 管理员：创建模板
  async createTemplate(data: Partial<Omit<PromptTemplateDetail, 'variables'> & { variables?: TemplateVariable[] }>): Promise<{ message: string; data: PromptTemplateDetail }> {
    const response = await authFetch('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 管理员：更新模板
  async updateTemplate(id: string, data: Partial<Omit<PromptTemplateDetail, 'variables'> & { variables?: TemplateVariable[] }>): Promise<{ message: string; data: PromptTemplateDetail }> {
    const response = await authFetch(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 管理员：删除模板
  async deleteTemplate(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/templates/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },

  // 管理员：AI分析提示词模板生成标题和分类
  async analyzeTemplate(templateText: string): Promise<{ title: string; category: string }> {
    const response = await authFetch('/templates/analyze', {
      method: 'POST',
      body: JSON.stringify({ templateText }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '分析失败');
    }
    return response.json();
  },
};

// ==================== 邀请奖励 API ====================

export interface InvitationInfo {
  inviteEnabled: boolean;
  inviterCredits: number;
  inviteeCredits: number;
  inviteCode: string;
  inviteLink: string;
  totalInvited: number;
  totalEarned: number;
  rebate: {
    enabled: boolean;
    percent: number;
    creditsPerYuan: number;
    minOrderAmount: number;
    monthlyCapCredits: number;
    totalCredits: number;
    totalReversed: number;
    count: number;
  };
  invitations: Array<{
    id: string;
    inviteeEmail: string;
    inviteeName: string | null;
    inviterBonus: number;
    inviteeBonus: number;
    createdAt: string;
  }>;
}

export interface InvitationConfig {
  inviteEnabled: boolean;
  inviterCredits: number;
  inviteeCredits: number;
  invitePhoneOnly: boolean;
  rebateEnabled?: boolean;
  rebatePercent?: number;
  rebateCreditsPerYuan?: number;
  rebateMinOrderAmount?: number;
}

export interface MyReferralRebateRecord {
  id: string;
  inviteeEmail: string;
  inviteeName: string | null;
  baseAmount: number;
  percent: number;
  credits: number;
  status: 'credited' | 'blocked' | 'reversed' | string;
  blockReason: string | null;
  reversedCredits: number;
  reversedAt: string | null;
  createdAt: string;
}

export interface ReferralRebateConfig {
  enabled: boolean;
  rebatePercent: number;
  minOrderAmount: number;
  maxCreditsPerOrder: number;
  monthlyCapCredits: number;
  blockSameIp: boolean;
  blockSameDevice: boolean;
  blockSameLoginIp: boolean;
  publicIpUserThreshold: number;
  exchangeRate: number;
  creditsPerYuan: number;
  sampleCredits: number;
}

export interface AdminReferralRebateRecord {
  id: string;
  inviterEmail: string;
  inviterName: string | null;
  inviteeEmail: string;
  inviteeName: string | null;
  orderId: string;
  outTradeNo: string;
  orderAmount: number;
  orderCredits: number;
  orderStatus: string;
  baseAmount: number;
  exchangeRate: number;
  percent: number;
  credits: number;
  status: 'credited' | 'blocked' | 'reversed' | string;
  blockReason: string | null;
  blockDetail: string | null;
  reversedCredits: number;
  shortfallCredits: number;
  reversedAt: string | null;
  createdAt: string;
}

export interface ReferralRebateStats {
  totalCredits: number;
  totalOrders: number;
  reversedCredits: number;
  shortfallCredits: number;
  blockedOrders: number;
  monthCredits: number;
  monthOrders: number;
  pendingRecalc: number;
  monthStart: string;
}

export interface AdminInvitationRecord {
  id: string;
  inviterEmail: string;
  inviterName: string | null;
  inviteeEmail: string;
  inviteeName: string | null;
  inviterBonus: number;
  inviteeBonus: number;
  createdAt: string;
}

export interface ImageFailureLogEntry {
  timestamp: string;
  logFile?: string;
  jobId?: string;
  userId?: string;
  model?: string;
  mode?: string;
  source?: string;
  requestUrl?: string;
  channelId?: string;
  channelName?: string;
  provider?: string;
  error?: unknown;
  providerResponse?: unknown;
}

export const invitationAPI = {
  // 获取当前用户的邀请信息
  async getMyInvitations(): Promise<InvitationInfo> {
    const response = await authFetch('/invitation/my');
    if (!response.ok) throw new Error('获取邀请信息失败');
    return response.json();
  },

  // 获取邀请配置（公开接口）
  async getInvitationConfig(): Promise<InvitationConfig> {
    const response = await fetch(`${API_BASE}/invitation/config`);
    if (!response.ok) throw new Error('获取邀请配置失败');
    return response.json();
  },

  // 管理员：获取所有邀请记录
  async getAdminInvitations(): Promise<{ data: AdminInvitationRecord[] }> {
    const response = await authFetch('/invitation/admin/all');
    if (!response.ok) throw new Error('获取邀请记录失败');
    return response.json();
  },

  // 我的充值返利明细
  async getMyRebates(page = 1, limit = 10): Promise<{
    data: MyReferralRebateRecord[];
    pagination: PaginationInfo;
  }> {
    const response = await authFetch(`/invitation/rebates?page=${page}&limit=${limit}`);
    if (!response.ok) throw new Error('获取返利明细失败');
    return response.json();
  },
};

// ==================== 推广返利（管理端） ====================

export const referralAdminAPI = {
  async getConfig(): Promise<ReferralRebateConfig> {
    const response = await authFetch('/admin/referral/config');
    if (!response.ok) throw new Error('读取推广返利配置失败');
    return response.json();
  },

  async updateConfig(payload: Partial<ReferralRebateConfig>): Promise<ReferralRebateConfig> {
    const response = await authFetch('/admin/referral/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || '保存推广返利配置失败');
    return data;
  },

  async getRebates(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{ data: AdminReferralRebateRecord[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    const response = await authFetch(`/admin/referral/rebates?${query.toString()}`);
    if (!response.ok) throw new Error('读取推广返利流水失败');
    return response.json();
  },

  async getStats(): Promise<ReferralRebateStats> {
    const response = await authFetch('/admin/referral/stats');
    if (!response.ok) throw new Error('读取推广返利统计失败');
    return response.json();
  },

  async reconcile(days = 7): Promise<{ scanned: number; credited: number; skipped: number }> {
    const response = await authFetch('/admin/referral/reconcile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || '补算失败');
    return data;
  },
};

export const adminLogAPI = {
  async getImageFailureLogs(limit = 100): Promise<{
    data: ImageFailureLogEntry[];
    meta: {
      limit: number;
      count: number;
      scannedFiles: string[];
    };
  }> {
    const query = new URLSearchParams({ limit: String(limit) });
    const response = await authFetch(`/admin/image-failure-logs?${query.toString()}`);
    if (!response.ok) throw new Error('获取图片失败日志失败');
    return response.json();
  },
};

export type UserFacingErrorRuleCategory =
  | 'moderation'
  | 'infrastructure'
  | 'generation'
  | 'param'
  | 'config'
  | 'unknown';

export interface UserFacingErrorRule {
  id: string;
  mediaType: 'all' | 'image' | 'video';
  matchType: 'contains';
  pattern: string;
  userMessage: string;
  priority: number;
  enabled: boolean;
  stopChain: boolean;
  category: UserFacingErrorRuleCategory;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserFacingErrorRuleInput {
  mediaType: 'all' | 'image' | 'video';
  matchType: 'contains';
  pattern: string;
  userMessage: string;
  priority: number;
  enabled: boolean;
  stopChain: boolean;
  category: UserFacingErrorRuleCategory;
  note?: string;
}

export const adminErrorRuleAPI = {
  async list(): Promise<{ data: UserFacingErrorRule[] }> {
    const response = await authFetch('/admin/error-rules');
    if (!response.ok) throw new Error('获取错误映射规则失败');
    return response.json();
  },

  async create(payload: UserFacingErrorRuleInput): Promise<{ data: UserFacingErrorRule }> {
    const response = await authFetch('/admin/error-rules', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '创建错误映射规则失败' }));
      throw new Error(error.error || '创建错误映射规则失败');
    }
    return response.json();
  },

  async update(id: string, payload: UserFacingErrorRuleInput): Promise<{ data: UserFacingErrorRule }> {
    const response = await authFetch(`/admin/error-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '更新错误映射规则失败' }));
      throw new Error(error.error || '更新错误映射规则失败');
    }
    return response.json();
  },

  async remove(id: string): Promise<{ ok: true }> {
    const response = await authFetch(`/admin/error-rules/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '删除错误映射规则失败' }));
      throw new Error(error.error || '删除错误映射规则失败');
    }
    return response.json();
  },

  async test(payload: {
    mediaType: 'image' | 'video';
    errorMessage: string;
  }): Promise<{
    data: {
      matchedRule: UserFacingErrorRule | null;
      analysisText: string;
      finalUserMessage: string | null;
    };
  }> {
    const response = await authFetch('/admin/error-rules/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '测试错误映射规则失败' }));
      throw new Error(error.error || '测试错误映射规则失败');
    }
    return response.json();
  },
};

// ==================== 风控黑名单 API ====================

export interface RiskBlacklistItem {
  id: string;
  type: string;
  value: string;
  reason: string | null;
  createdAt: string;
  createdBy: string | null;
}

export const blacklistAPI = {
  async list(params?: { page?: number; limit?: number; type?: string; search?: string }): Promise<{
    data: RiskBlacklistItem[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.type) query.set('type', params.type);
    if (params?.search) query.set('search', params.search);
    const response = await authFetch(`/admin/blacklist?${query.toString()}`);
    if (!response.ok) throw new Error('获取黑名单失败');
    return response.json();
  },

  async create(payload: { type: string; value: string; reason?: string }): Promise<{ data: RiskBlacklistItem; message: string }> {
    const response = await authFetch('/admin/blacklist', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '添加黑名单失败' }));
      throw new Error(error.error || '添加黑名单失败');
    }
    return response.json();
  },

  async remove(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/admin/blacklist/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '删除黑名单失败' }));
      throw new Error(error.error || '删除黑名单失败');
    }
    return response.json();
  },
};

// ==================== 我的作品库 API ====================

export interface WorkItem {
  id: string;
  title: string | null;
  prompt: string;
  model: string;
  size: string;
  quality: string;
  mode: string;
  status: string;
  source: string;
  resultUrl: string | null;
  /** 服务端补全的 small 缩略图地址，列表场景优先使用 */
  thumbnailUrl: string | null;
  /** 服务端补全的 medium 缩略图地址，详情/中尺寸场景使用 */
  thumbnailMediumUrl: string | null;
  referenceImages: string[] | null;
  maskImage: string | null;
  creditsUsed: number;
  isFavorite: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkDetail extends WorkItem {
  description: string | null;
  publicTitle: string | null;
  publicDescription: string | null;
  isPromptPublic: boolean;
  errorMessage: string | null;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const worksAPI = {
  // 获取我的作品列表
  async getWorks(params?: {
    mode?: string;
    model?: string;
    size?: string;
    quality?: string;
    status?: string;
    isFavorite?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: WorkItem[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.mode) query.set('mode', params.mode);
    if (params?.model) query.set('model', params.model);
    if (params?.size) query.set('size', params.size);
    if (params?.quality) query.set('quality', params.quality);
    if (params?.status) query.set('status', params.status);
    if (params?.isFavorite) query.set('isFavorite', 'true');
    if (params?.search) query.set('search', params.search);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const response = await authFetch(`/works?${query.toString()}`);
    if (!response.ok) throw new Error('获取作品列表失败');
    return response.json();
  },

  // 获取作品详情
  async getWork(id: string): Promise<WorkDetail> {
    const response = await authFetch(`/works/${id}`);
    if (!response.ok) throw new Error('获取作品详情失败');
    const data = await response.json();
    return data;
  },

  // 更新作品信息
  async updateWork(id: string, data: { title?: string; description?: string; isFavorite?: boolean }): Promise<{ message: string }> {
    const response = await authFetch(`/works/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 隐藏作品
  async hideWork(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/works/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '隐藏失败');
    }
    return response.json();
  },

  // 公开作品到广场
  async publishWork(id: string, data: { publicTitle?: string; publicDescription?: string; publicCategory?: string; isPromptPublic?: boolean }): Promise<{ message: string }> {
    const response = await authFetch(`/works/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '公开失败');
    }
    return response.json();
  },

  // 取消公开
  async unpublishWork(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/works/${id}/unpublish`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '取消公开失败');
    }
    return response.json();
  },

  // AI分析作品生成标题和分类
  async analyzeWork(prompt: string): Promise<{ title: string; category: string }> {
    const response = await authFetch('/works/analyze', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '分析失败');
    }
    return response.json();
  },
};

// ==================== 公共作品广场 API ====================

export interface GalleryWork {
  id: string;
  publicTitle: string;
  publicDescription: string;
  publicCategory: string;
  resultUrl: string | null;
  /** 匿名可读的公开浏览地址（广场展示用，私有网关 resultUrl 仅登录态可用） */
  publicImageUrl?: string | null;
  /** 服务端补全的 small 缩略图地址 */
  thumbnailUrl: string | null;
  /** 服务端补全的 medium 缩略图地址 */
  thumbnailMediumUrl: string | null;
  model: string;
  size: string;
  quality: string;
  mode: string;
  isPromptPublic: boolean;
  prompt: string | null;
  likeCount: number;
  viewCount: number;
  publishedAt: string | null;
  reportCount?: number;
  user: {
    id: string;
    name: string;
  };
}

export interface GalleryWorkDetail extends GalleryWork {
  // 详情与列表结构相同
}

export const galleryAPI = {
  // 获取公共作品列表（公开接口，无需登录）
  async getPublicWorks(params?: {
    sort?: 'latest' | 'hot';
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: GalleryWork[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.sort) query.set('sort', params.sort);
    if (params?.category && params.category !== 'all') query.set('category', params.category);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const response = await fetch(`${API_BASE}/gallery/public?${query.toString()}`);
    if (!response.ok) throw new Error('获取作品列表失败');
    return response.json();
  },

  // 获取公共作品详情
  async getPublicWork(id: string): Promise<GalleryWorkDetail> {
    const response = await fetch(`${API_BASE}/gallery/public/${id}`);
    if (!response.ok) throw new Error('获取作品详情失败');
    return response.json();
  },

  // 获取当前用户对作品的互动状态
  async getInteractions(id: string): Promise<{ isLiked: boolean; isFavorited: boolean }> {
    const response = await authFetch(`/gallery/public/${id}/interactions`);
    if (!response.ok) throw new Error('获取互动状态失败');
    return response.json();
  },

  // 点赞
  async likeWork(id: string): Promise<{ message: string; isLiked: boolean }> {
    const response = await authFetch(`/gallery/public/${id}/like`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '点赞失败');
    }
    return response.json();
  },

  // 取消点赞
  async unlikeWork(id: string): Promise<{ message: string; isLiked: boolean }> {
    const response = await authFetch(`/gallery/public/${id}/like`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '取消点赞失败');
    }
    return response.json();
  },

  // 收藏
  async favoriteWork(id: string): Promise<{ message: string; isFavorited: boolean }> {
    const response = await authFetch(`/gallery/public/${id}/favorite`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '收藏失败');
    }
    return response.json();
  },

  // 取消收藏
  async unfavoriteWork(id: string): Promise<{ message: string; isFavorited: boolean }> {
    const response = await authFetch(`/gallery/public/${id}/favorite`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '取消收藏失败');
    }
    return response.json();
  },

  // 举报
  async reportWork(id: string, reason?: string): Promise<{ message: string }> {
    const response = await authFetch(`/gallery/public/${id}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '举报失败');
    }
    return response.json();
  },

  // 获取我的点赞
  async getMyLikes(params?: { page?: number; limit?: number }): Promise<{ data: GalleryWork[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const response = await authFetch(`/gallery/my-likes?${query.toString()}`);
    if (!response.ok) throw new Error('获取点赞列表失败');
    return response.json();
  },

  // 获取我的收藏
  async getMyFavorites(params?: { page?: number; limit?: number }): Promise<{ data: GalleryWork[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const response = await authFetch(`/gallery/my-favorites?${query.toString()}`);
    if (!response.ok) throw new Error('获取收藏列表失败');
    return response.json();
  },

  // AI融合同款生成
  async remixWork(id: string, userRequirement: string): Promise<{ prompt: string; originalPrompt: string }> {
    const response = await authFetch(`/gallery/public/${id}/remix`, {
      method: 'POST',
      body: JSON.stringify({ userRequirement }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '融合生成失败');
    }
    return response.json();
  },

  // AI融合同款生成 - 流式输出
  async remixWorkStream(
    id: string,
    userRequirement: string,
    onChunk: (chunk: string) => void,
    onError?: (error: string) => void,
    onComplete?: () => void
  ): Promise<void> {
    const response = await platformFetch(`/gallery/public/${id}/remix/stream`, {
      method: 'POST',
      body: JSON.stringify({ userRequirement }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(error.error || '融合生成失败');
    }

    if (!response.body) {
      throw new Error('无法读取响应');
    }

    await streamJsonEvents(response, {
      onEvent: (parsed) => {
        if (parsed.error) {
          onError?.(parsed.error as string);
        } else if (parsed.content) {
          onChunk(parsed.content as string);
        }
      },
      onDone: () => onComplete?.(),
    });
  },

  // 管理员：获取被举报的作品列表
  async getReportedWorks(params?: { page?: number; limit?: number }): Promise<{ data: GalleryWork[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const response = await authFetch(`/gallery/admin/reported?${query.toString()}`);
    if (!response.ok) throw new Error('获取举报列表失败');
    return response.json();
  },

  // 管理员：下架作品
  async unpublishWork(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/gallery/admin/${id}/unpublish`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '下架失败');
    }
    return response.json();
  },

  // 管理员：清空举报次数
  async clearWorkReports(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/gallery/admin/${id}/clear-reports`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '清空举报失败');
    }
    return response.json();
  },
};

// ==================== 图片迭代记录 API ====================

// ==================== 支付系统 API ====================

// 支付配置
export interface PaymentConfig {
  isEnabled: boolean;
  apiUrl: string;
  merchantId: string;
  platformPublicKey: string;
  merchantPrivateKey: string;
  merchantMd5Key: string;
  signType: string;
  notifyUrl: string | null;
  returnUrl: string | null;
  creditExchangeRate: number;
  enabledPayTypes: string[];
  minRechargeAmount: number;
  updatedAt: string;
}

// 充值套餐
export interface RechargePackage {
  id: string;
  name: string;
  amount: number;
  credits: number;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// 支付订单
export interface PaymentOrder {
  id: string;
  outTradeNo: string;
  amount: number;
  credits: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  payType: string | null;
  paidAt: string | null;
  createdAt: string;
}

// 支付配置 API
export const paymentConfigAPI = {
  // 获取支付配置（管理员）
  async getConfig(): Promise<PaymentConfig> {
    const response = await authFetch('/payment-config');
    if (!response.ok) throw new Error('获取支付配置失败');
    return response.json();
  },

  // 更新支付配置（管理员）
  async updateConfig(data: Partial<PaymentConfig>): Promise<{ message: string } & PaymentConfig> {
    const response = await authFetch('/payment-config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 测试支付配置（管理员）
  async testConfig(data: Partial<PaymentConfig>): Promise<{ message: string }> {
    const response = await authFetch('/payment-config/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '测试失败');
    }
    return response.json();
  },

  // 获取支付状态（公开）
  async getStatus(): Promise<{ isEnabled: boolean; enabledPayTypes: string[]; minRechargeAmount: number }> {
    const response = await fetch(`${API_BASE}/payment-config/status`);
    if (!response.ok) throw new Error('获取支付状态失败');
    return response.json();
  },
};

// 充值套餐 API
export const rechargePackageAPI = {
  // 获取所有充值套餐（公开）
  async getPackages(): Promise<RechargePackage[]> {
    const response = await fetch(`${API_BASE}/recharge-packages`);
    if (!response.ok) throw new Error('获取充值套餐失败');
    return response.json();
  },

  // 获取所有充值套餐（管理员）
  async getAdminPackages(): Promise<RechargePackage[]> {
    const response = await authFetch('/recharge-packages/admin');
    if (!response.ok) throw new Error('获取充值套餐失败');
    return response.json();
  },

  // 创建充值套餐（管理员）
  async createPackage(data: Partial<RechargePackage>): Promise<{ message: string; package: RechargePackage }> {
    const response = await authFetch('/recharge-packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建失败');
    }
    return response.json();
  },

  // 更新充值套餐（管理员）
  async updatePackage(id: string, data: Partial<RechargePackage>): Promise<{ message: string; package: RechargePackage }> {
    const response = await authFetch(`/recharge-packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新失败');
    }
    return response.json();
  },

  // 删除充值套餐（管理员）
  async deletePackage(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/recharge-packages/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除失败');
    }
    return response.json();
  },
};

// 用户支付 API
export const paymentAPI = {
  // 创建支付订单
  async createOrder(params: {
    packageId?: string;
    payType: string;
    customAmount?: number;
  }): Promise<{ orderId: string; outTradeNo: string; amount: number; credits: number; payUrl: string }> {
    const response = await authFetch('/payment/create', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建订单失败');
    }
    return response.json();
  },

  // 查询订单状态
  async getOrderStatus(outTradeNo: string): Promise<PaymentOrder> {
    const response = await authFetch(`/payment/order/${outTradeNo}`);
    if (!response.ok) throw new Error('查询订单失败');
    return response.json();
  },

  // 获取我的订单列表
  async getMyOrders(params?: { page?: number; limit?: number }): Promise<{ orders: PaymentOrder[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const response = await authFetch(`/payment/orders?${query.toString()}`);
    if (!response.ok) throw new Error('获取订单列表失败');
    return response.json();
  },
};

// ==================== 签到 API ====================

export interface CheckInConfig {
  enabled: boolean;
  minCredits: number;
  maxCredits: number;
}

export interface CheckInStatus {
  checkedInToday: boolean;
  todayCredits: number | null;
  consecutiveDays: number;
}

export interface CheckInRecord {
  id: string;
  credits: number;
  checkInAt: string;
}

export interface CheckInHistory {
  checkIns: CheckInRecord[];
  pagination: PaginationInfo;
}

export const checkinAPI = {
  // 获取签到配置（公开）
  async getConfig(): Promise<CheckInConfig> {
    const response = await fetch(`${API_BASE}/checkin/config`);
    if (!response.ok) throw new Error('获取签到配置失败');
    return response.json();
  },

  // 获取今日签到状态
  async getStatus(): Promise<CheckInStatus> {
    const response = await authFetch('/checkin/status');
    if (!response.ok) throw new Error('获取签到状态失败');
    return response.json();
  },

  // 执行签到
  async checkIn(): Promise<{ message: string; credits: number; totalCredits: number }> {
    const response = await authFetch('/checkin', {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '签到失败');
    }
    return response.json();
  },

  // 获取签到历史
  async getHistory(params?: { page?: number; limit?: number }): Promise<CheckInHistory> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const response = await authFetch(`/checkin/history?${query.toString()}`);
    if (!response.ok) throw new Error('获取签到历史失败');
    return response.json();
  },
};

// 支付订单管理 API（管理员）
export interface PaymentOrderAdmin extends PaymentOrder {
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  tradeNo: string | null;
  refundAmount: number;
  refundedAt: string | null;
  notifyData: string | null;
}

export const paymentAdminAPI = {
  // 获取所有支付订单
  async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    userId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    orders: PaymentOrderAdmin[];
    stats: { totalAmount: number; totalCredits: number; totalOrders: number };
    pagination: PaginationInfo;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.userId) query.set('userId', params.userId);
    if (params?.search) query.set('search', params.search);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const response = await authFetch(`/payment-admin/orders?${query.toString()}`);
    if (!response.ok) throw new Error('获取订单列表失败');
    return response.json();
  },

  // 获取订单详情
  async getOrderDetail(id: string): Promise<PaymentOrderAdmin> {
    const response = await authFetch(`/payment-admin/orders/${id}`);
    if (!response.ok) throw new Error('获取订单详情失败');
    return response.json();
  },

  // 手动补单
  async completeOrder(id: string): Promise<{ message: string }> {
    const response = await authFetch(`/payment-admin/orders/${id}/complete`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '补单失败');
    }
    return response.json();
  },

  // 退款
  async refundOrder(id: string, params?: { refundAmount?: number; reason?: string }): Promise<{ message: string }> {
    const response = await authFetch(`/payment-admin/orders/${id}/refund`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '退款失败');
    }
    return response.json();
  },

  // 获取支付统计
  async getStats(params?: { startDate?: string; endDate?: string }): Promise<{
    total: { amount: number; credits: number; orders: number };
    byPayType: Array<{ payType: string; amount: number; credits: number; orders: number }>;
    daily: Array<{ date: string; amount: number; orders: number }>;
  }> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    const response = await authFetch(`/payment-admin/stats?${query.toString()}`);
    if (!response.ok) throw new Error('获取统计失败');
    return response.json();
  },
};